import { ProcurementState } from "@boark/shared/types/procurement";
import { ProcurementCreate } from "@boark/shared/types/procurement-create";
import { ProcurementOrganization } from "@boark/shared/types/procurement-organization";
import { ProcurementOrganizationCreateOrUpdate } from "@boark/shared/types/procurement-organization-create-or-update";
import { ProcurementUpdate } from "@boark/shared/types/procurement-update";
import {
  ProcurementWithCreatedByAndOrganizations,
  procurementWithCreatedByAndOrganizationsSchema,
} from "@boark/shared/types/procurement-with-created-by-and-organizations";
import * as z from "zod";

import { formatPrice } from "../utils/format-price";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "../utils/request";
import { formatOrganizationLabel } from "./organization";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

export type ProcurementFormOrganization = {
  organizationId: string;
  price: string;
};

export function normalizeProcurementFormTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const t = raw.trim();
    if (t === "") {
      continue;
    }
    const key = t.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(t);
  }
  return result;
}

function formOrganizationsToPayload(
  organizations: ProcurementFormOrganization[],
): ProcurementOrganizationCreateOrUpdate[] {
  return organizations.map((organization) => ({
    organizationId: organization.organizationId,
    ...(organization.price.trim() !== ""
      ? { price: Number(organization.price) }
      : {}),
  }));
}

export function procurementFormValuesToCreate(values: {
  title: string;
  description: string;
  tags: string[];
  organizations: ProcurementFormOrganization[];
}): ProcurementCreate {
  const organizations = formOrganizationsToPayload(values.organizations);
  return {
    title: values.title.trim(),
    ...(values.description.trim() !== ""
      ? { description: values.description.trim() }
      : {}),
    state: ProcurementState.OPEN,
    tags: normalizeProcurementFormTags(values.tags),
    ...(organizations.length > 0 ? { organizations } : {}),
  };
}

export function procurementFormValuesToUpdate(values: {
  title: string;
  description: string;
  tags: string[];
  organizations: ProcurementFormOrganization[];
}): ProcurementUpdate {
  const organizations = formOrganizationsToPayload(values.organizations);
  return {
    title: values.title.trim(),
    description:
      values.description.trim() === "" ? null : values.description.trim(),
    tags: normalizeProcurementFormTags(values.tags),
    organizations: organizations.length > 0 ? organizations : null,
  };
}

export function formatProcurementOrganizationLabel(
  organization: ProcurementOrganization,
  locale: "en" | "sv" = "sv",
): string {
  const label = formatOrganizationLabel(organization.organization);
  if (organization.price === undefined) {
    return label;
  }
  return `${label} (${formatPrice(organization.price, locale)})`;
}

export function uniqueSortedTagSuggestions(
  procurements: { tags: string[] }[] | undefined,
): string[] {
  if (!procurements?.length) {
    return [];
  }
  const unique = new Set<string>();
  for (const procurement of procurements) {
    for (const tag of procurement.tags) {
      const trimmed = tag.trim();
      if (trimmed !== "") {
        unique.add(trimmed);
      }
    }
  }
  return [...unique].sort((left, right) => left.localeCompare(right));
}

export async function getProcurements(): Promise<
  ProcurementWithCreatedByAndOrganizations[]
> {
  return getRequest(
    `${API_BASE_URL}/procurement`,
    z.array(procurementWithCreatedByAndOrganizationsSchema),
  );
}

export async function getProcurement(
  id: string,
): Promise<ProcurementWithCreatedByAndOrganizations> {
  return getRequest(
    `${API_BASE_URL}/procurement/${id}`,
    procurementWithCreatedByAndOrganizationsSchema,
  );
}

export async function createProcurement(
  procurement: ProcurementCreate,
): Promise<ProcurementWithCreatedByAndOrganizations> {
  return postRequest(
    `${API_BASE_URL}/procurement`,
    procurement,
    procurementWithCreatedByAndOrganizationsSchema,
  );
}

export async function updateProcurement(
  id: string,
  procurement: ProcurementUpdate,
): Promise<ProcurementWithCreatedByAndOrganizations> {
  return putRequest(
    `${API_BASE_URL}/procurement/${id}`,
    procurement,
    procurementWithCreatedByAndOrganizationsSchema,
  );
}

export async function deleteProcurement(id: string): Promise<void> {
  return deleteRequest(`${API_BASE_URL}/procurement/${id}`);
}
